import React, { useState, useEffect, useRef } from 'react';
import {
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Alert,
  Checkbox,
  Row,
  Col,
} from 'antd';
import { Helmet } from 'react-helmet';
import { SearchOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { TabPane } = Tabs;
const { Option } = Select;
const { useWatch } = Form;

const CategoryManagement = () => {
  const [activeTab, setActiveTab] = useState('categories');
  const [data, setData] = useState({
    categories: [],
    childCategories: [],
    subchildCategories: [],
  });
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const [form] = Form.useForm();
  const parentCategoryId = useWatch('parent_category_id', form);

  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Search functionality states
  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  const userTypeOptions = [
    { value: '3rd Party Services', label: '3rd Party Services' },
    { value: 'Partner', label: 'Partner' },
    { value: 'Employee', label: 'Skilled Business Employee' },
    { value: 'Business', label: 'Business For Sale' },
  ];

  // Search functions
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
          autoFocus
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => {
      if (searchedColumn === dataIndex && searchText) {
        const regex = new RegExp(searchText, 'gi');
        const parts = String(text).split(regex);
        return (
          <>
            {parts.map((part, index) =>
              index > 0 ? (
                <span key={index} style={{ backgroundColor: '#ffc069' }}>
                  {searchText}
                </span>
              ) : (
                part
              )
            )}
          </>
        );
      }
      return text;
    },
  });

  const clearMessages = () => {
    setSuccessMsg('');
    setErrorMsg('');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, childRes, subchildRes] = await Promise.all([
        api.get('/categories'),
        api.get('/child-categories'),
        api.get('/subchild-categories'),
      ]);
      setData({
        categories: Array.isArray(catRes.data) ? catRes.data : [],
        childCategories: Array.isArray(childRes.data) ? childRes.data : [],
        subchildCategories: Array.isArray(subchildRes.data)
          ? subchildRes.data
          : [],
      });
    } catch (error) {
      setErrorMsg('Failed to fetch category data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    clearMessages();
    form.resetFields();
    setVisible(false);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      clearMessages();

      let url = '';
      let method = '';
      let payload = {};

      if (activeTab === 'categories') {
        url = editingId ? `/categories/${editingId}` : `/categories`;
        method = editingId ? 'put' : 'post';
        payload = { 
          name: values.name,
          user_types: values.user_types || [] // Changed to array
        };
      } else if (activeTab === 'child-categories') {
        url = editingId
          ? `/child-categories/${editingId}`
          : `/child-categories`;
        method = editingId ? 'put' : 'post';
        payload = {
          name: values.name,
          parent_category_id: values.parent_category_id,
        };
      } else if (activeTab === 'subchild-categories') {
        url = editingId
          ? `/subchild-categories/${editingId}`
          : `/subchild-categories`;
        method = editingId ? 'put' : 'post';
        payload = {
          name: values.name,
          parent_category_id: values.parent_category_id,
          child_category_id: values.child_category_id,
        };
      }

      await api[method](url, payload);

      setSuccessMsg(`${editingId ? 'Updated' : 'Created'} successfully`);
      setVisible(false);
      setEditingId(null);
      form.resetFields();
      fetchData();
    } catch (error) {
      console.error('Submit error:', error);
      setErrorMsg(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    clearMessages();
    Modal.confirm({
      title: 'Are you sure you want to delete this item?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        setLoading(true);
        try {
          let url = '';
          if (activeTab === 'categories') url = `/categories/${id}`;
          else if (activeTab === 'child-categories')
            url = `/child-categories/${id}`;
          else if (activeTab === 'subchild-categories')
            url = `/subchild-categories/${id}`;

          await api.delete(url);
          setSuccessMsg('Deleted successfully');
          fetchData();
        } catch (error) {
          console.error('Delete error:', error);
          setErrorMsg(error.response?.data?.message || 'Delete failed');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleEdit = (record) => {
    clearMessages();
    setEditingId(record.id);
    setVisible(true);

    if (activeTab === 'categories') {
      form.setFieldsValue({ 
        name: record.name,
        user_types: record.user_types ? record.user_types.split(',') : [] // Convert string to array
      });
    } else if (activeTab === 'child-categories') {
      form.setFieldsValue({
        name: record.name,
        parent_category_id: record.parent_category_id,
      });
    } else if (activeTab === 'subchild-categories') {
      form.setFieldsValue({
        name: record.name,
        parent_category_id: record.parent_category_id,
        child_category_id: record.child_category_id,
      });
    }
  };

  const getFilteredCategories = () => {
    return data.categories;
  };

  const getColumns = () => {
    if (activeTab === 'categories') {
      return [
        {
          title: 'S.No.',
          key: 'sno',
          width: 80,
          render: (_text, _record, index) =>
            index + 1 + (pagination.current - 1) * pagination.pageSize,
        },
        { 
          title: 'Name', 
          dataIndex: 'name', 
          key: 'name',
          ...getColumnSearchProps('name'),
        },
        { 
          title: 'User Types', 
          dataIndex: 'user_types', 
          key: 'user_types',
          render: (user_types) => {
            if (!user_types) return '-';
            const typesArray = user_types.split(',');
            return typesArray.join(', ');
          },
          ...getColumnSearchProps('user_types'),
        },
        {
          title: 'Actions',
          key: 'actions',
          render: (_, record) => (
            <Space>
              <Button onClick={() => handleEdit(record)}>Edit</Button>
              <Button danger onClick={() => handleDelete(record.id)}>
                Delete
              </Button>
            </Space>
          ),
        },
      ];
    } else if (activeTab === 'child-categories') {
      return [
        {
          title: 'S.No.',
          key: 'sno',
          width: 80,
          render: (_text, _record, index) =>
            index + 1 + (pagination.current - 1) * pagination.pageSize,
        },
        { 
          title: 'Name', 
          dataIndex: 'name', 
          key: 'name',
          ...getColumnSearchProps('name'),
        },
        {
          title: 'Parent Category',
          dataIndex: 'parent_category_name',
          key: 'parent_category_name',
          ...getColumnSearchProps('parent_category_name'),
        },
        {
          title: 'Actions',
          key: 'actions',
          render: (_, record) => (
            <Space>
              <Button onClick={() => handleEdit(record)}>Edit</Button>
              <Button danger onClick={() => handleDelete(record.id)}>
                Delete
              </Button>
            </Space>
          ),
        },
      ];
    } else {
      return [
        {
          title: 'S.No.',
          key: 'sno',
          width: 80,
          render: (_text, _record, index) =>
            index + 1 + (pagination.current - 1) * pagination.pageSize,
        },
        { 
          title: 'Name', 
          dataIndex: 'name', 
          key: 'name',
          ...getColumnSearchProps('name'),
        },
        {
          title: 'Parent Category',
          dataIndex: 'parent_category_name',
          key: 'parent_category_name',
          ...getColumnSearchProps('parent_category_name'),
        },
        {
          title: 'Child Category',
          dataIndex: 'child_category_name',
          key: 'child_category_name',
          ...getColumnSearchProps('child_category_name'),
        },
        {
          title: 'Actions',
          key: 'actions',
          render: (_, record) => (
            <Space>
              <Button onClick={() => handleEdit(record)}>Edit</Button>
              <Button danger onClick={() => handleDelete(record.id)}>
                Delete
              </Button>
            </Space>
          ),
        },
      ];
    }
  };

  return (
    <>
      <Helmet>
        <title>Categories Management</title>
      </Helmet>
      
      <div style={{ padding: '20px' }}>
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Categories" key="categories" />
          <TabPane tab="Child Categories" key="child-categories" />
          <TabPane tab="Subchild Categories" key="subchild-categories" />
        </Tabs>

        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            onClick={() => {
              clearMessages();
              setVisible(true);
              form.resetFields();
              setEditingId(null);
            }}
          >
            Add New
          </Button>
        </div>

        {successMsg && (
          <Alert
            message={successMsg}
            type="success"
            closable
            onClose={() => setSuccessMsg('')}
            style={{ marginBottom: 16 }}
          />
        )}
        {errorMsg && (
          <Alert
            message={errorMsg}
            type="error"
            closable
            onClose={() => setErrorMsg('')}
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          columns={getColumns()}
          dataSource={activeTab === 'categories'
            ? data.categories
            : activeTab === 'child-categories'
            ? data.childCategories
            : data.subchildCategories}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
          }}
          scroll={{ x: 'max-content' }}
        />

        <Modal
          visible={visible}
          title={
            editingId
              ? `Edit ${activeTab.replace(/-/g, ' ')}`
              : `Add New ${activeTab.replace(/-/g, ' ')}`
          }
          onCancel={() => setVisible(false)}
          onOk={handleSubmit}
          confirmLoading={loading}
          destroyOnClose
          width={600}
        >
          <Form form={form} layout="vertical">
            {activeTab === 'categories' && (
              <Form.Item
                label="User Types"
                name="user_types"
                rules={[
                  { 
                    required: true, 
                    message: 'Please select at least one user type!' 
                  },
                  {
                    validator: (_, value) => 
                      !value || value.length === 0 
                        ? Promise.reject(new Error('Please select at least one user type!'))
                        : Promise.resolve()
                  }
                ]}
              >
                <Checkbox.Group style={{ width: '100%' }}>
                  <Row>
                    {userTypeOptions.map(option => (
                      <Col span={12} key={option.value}>
                        <Checkbox value={option.value}>{option.label}</Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              </Form.Item>
            )}

            <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: 'Please input category name!' },
              ]}
            >
              <Input />
            </Form.Item>

            {(activeTab === 'child-categories' ||
              activeTab === 'subchild-categories') && (
              <Form.Item
                label="Parent Category"
                name="parent_category_id"
                rules={[
                  { required: true, message: 'Please select parent category!' },
                ]}
              >
                <Select 
                  placeholder="Select parent category" 
                  allowClear
                >
                  {getFilteredCategories().map((cat) => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {activeTab === 'subchild-categories' && (
              <Form.Item
                label="Child Category"
                name="child_category_id"
                rules={[
                  { required: true, message: 'Please select child category!' },
                ]}
              >
                <Select
                  placeholder="Select child category"
                  allowClear
                  disabled={!parentCategoryId}
                >
                  {data.childCategories
                    .filter(
                      (cc) => cc.parent_category_id === parentCategoryId
                    )
                    .map((cc) => (
                      <Option key={cc.id} value={cc.id}>
                        {cc.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            )}
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default CategoryManagement;